import { Typography, useEventCallback } from '@mui/material';
import {
  NotificationsList,
  ResearchNotifications,
} from 'notifications-from-civil-status/view-models/notifications-list';
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import AccordionFilter from 'notifications-from-civil-status/components/research-notifications/filter';
import { transformNotificationsDataset } from 'notifications-from-civil-status/utils/transform-notifications-dataset';
import { isObjectEmpty } from 'shell/utils/is-object-empty';
import TableNotifications from 'notifications-from-civil-status/components/research-notifications/table';
import { IdAmbito } from 'shell/constants/id-ambito';
import { NotificaResponse } from 'notifications-from-civil-status/model/notifica';
import { transformerNotificaList } from 'notifications-from-civil-status/utils/transformer-notifica-list';
import { useParams } from 'react-router-dom';
import { ID_REGISTER } from 'civil-status/components/shared/list-register';

interface ResearchNotificationsSharedProps {
  scope?: string;
  title?: string;
  items?: BreadcrumbItem[];
  baseUrl?: string;
  endpointUrl?: string;
  endpointParams?: ResearchNotifications;
  addedActions?: (item: NotificationsList) => ReactElement;
  checkVisibilityActions?: { read?: boolean; edit?: boolean; delete?: boolean };
}

const ResearchNotificationsShared = ({
  scope,
  title = 'Ricerca Registrazioni SC - Anagrafe',
  items,
  baseUrl,
  endpointUrl = `${__ACT_API_URL__}/aggiornamento-anagrafe/list`,
  endpointParams,
  addedActions,
  checkVisibilityActions,
}: ResearchNotificationsSharedProps): ReactElement => {
  const filterTable = useFilterTable<ResearchNotifications>();

  const params = useParams();
  const idCatParams = params.idCat || undefined;

  const idCat: string = useMemo(() => {
    if (idCatParams) {
      return idCatParams;
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return ID_REGISTER.NASCITA;
      case IdAmbito.Cancellazioni:
        return ID_REGISTER.MORTE;
      default:
        return '';
    }
  }, [idCatParams, scope]);

  // filtri
  const [open, setOpen] = useState<boolean>(true);
  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<ResearchNotifications | undefined>({});
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [notificationsList, getNotificationsList] = useHttpFetch<
    HttpResponse<Array<NotificaResponse>>,
    Array<NotificationsList>
  >(endpointUrl, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerNotificaList,
  });

  const handleResearch = useEventCallback(
    (values: ResearchNotifications, page?: number, rowsPerPage?: number) => {
      const newFilters: ResearchNotifications = {
        typeEvent: values.typeEvent,
        code: values.code,
        dateEnd: values.dateEnd,
        dateStart: values.dateStart,
        owners: values.owners,
        scope: values.scope,
        state: values.state,
        codOperator: values.codOperator,
        municipalityFormation: values.municipalityFormation,
        tableProps: {
          page,
          rowsPerPage,
        },
        ...(endpointParams || {}),
      };

      // mantenere dati
      setFilterSelected(newFilters);
      filterTable.setValues(newFilters);

      const queryString = transformNotificationsDataset(newFilters, page, rowsPerPage);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getNotificationsList({
        params: {},
        paramsSerializer: () => queryString,
      });
    }
  );

  useEffect(() => {
    if (((notificationsList.data as Array<NotificationsList>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpen(false);
    }
  }, [notificationsList.data]);

  useEffect(() => {
    if (!isObjectEmpty((filterTable.values || {}) as Record<string, unknown>)) {
      handleResearch(
        filterTable.values || {},
        filterTable.values?.tableProps?.page || 0,
        filterTable.values?.tableProps?.rowsPerPage || 10
      );
    }
  }, []);

  return (
    <Fragment>
      <Breadcrumbs items={items || []} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          {title}
        </Typography>
      </div>
      <AccordionFilter
        setOpen={setOpen}
        open={open}
        handleSubmitResearch={handleResearch}
        scope={scope}
        idCat={idCat}
        withOperator
      />
      {notificationsList.data ? (
        ((notificationsList.data as Array<NotificationsList>) || [])?.length > 0 ? (
          <TableNotifications
            data={notificationsList.data as Array<NotificationsList>}
            filterSelected={filterSelected}
            getNotificationsListData={handleResearch}
            tableProps={tableProps}
            baseUrl={baseUrl}
            addedActions={addedActions}
            checkVisibilityActions={checkVisibilityActions}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ResearchNotificationsShared.displayName = 'Research Notifications Shared component';

export default ResearchNotificationsShared;
